'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useToast } from '@/hooks/use-toast';
import { authApi } from '@/lib/medusa-client';
import { signOut as firebaseSignOut } from '@/lib/firebase';

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  has_account: boolean;
  metadata?: Record<string, any>;
}

interface AuthContextType {
  customer: Customer | null;
  loading: boolean;
  login: (phone: string, otp: string, name?: string) => Promise<void>;
  loginWithFirebase: (idToken: string, name?: string) => Promise<void>;
  loginWithTruecaller: (phoneNumber: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const { toast } = useToast();

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch('/api/auth', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCustomer(data.customer);
      } else {
        // Don't re-set null if already null, avoids unnecessary re-render
        if (customer !== null) setCustomer(null);
        const errorData = await response.json().catch(() => ({}));
        console.log('❌ Authentication failed:', response.status, errorData);
      }
    } catch (error) {
      console.error('❌ Error checking auth:', error);
      if (customer !== null) setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  const login = useCallback(async (phone: string, otp: string, name?: string) => {
    const response = await fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ action: 'otp-verify', phone, otp, name }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    setCustomer(data.customer);

    toast({
      title: "Welcome!",
      description: data.message || "Login successful",
    });
  }, [toast]);

  const loginWithFirebase = useCallback(async (idToken: string, name?: string) => {
    const response = await fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ action: 'firebase', idToken, name }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    setCustomer(data.customer);

    toast({
      title: "Welcome!",
      description: data.message || "Login successful",
    });
  }, [toast]);

  const loginWithTruecaller = useCallback(async (phoneNumber: string) => {
    const response = await fetch('/api/auth/truecaller', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phoneNumber }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Truecaller login failed');
    }

    setCustomer(data.customer);

    toast({
      title: "Welcome!",
      description: data.message || "Login successful with Truecaller",
    });
  }, [toast]);

  const logout = useCallback(async () => {
    try {
      // Sign out from Firebase
      await firebaseSignOut();

      // Sign out from backend via our auth API
      await fetch('/api/auth', {
        method: 'DELETE',
        credentials: 'include',
      });

      setCustomer(null);
      router.push('/');

      toast({
        title: "Logged out",
        description: "You have been logged out successfully",
      });
    } catch (error) {
      console.error('Error logging out:', error);
      // Still clear local state even if logout fails
      setCustomer(null);
      router.push('/');
    }
  }, [router, toast]);

  useEffect(() => {
    checkAuth();
  }, []);

  const value = useMemo(() => ({
    customer, loading, login, loginWithFirebase, loginWithTruecaller, logout, checkAuth
  }), [customer, loading, login, loginWithFirebase, loginWithTruecaller, logout, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 